import { useState, useEffect } from 'react';
import api from '../api';
import useLogMutations from './useLogMutations';

// Owns chore/log/roommate/user server state and the mutations that touch it.
// Callers that need to react to success (closing a modal, resetting a form)
// get a boolean/result back instead of the hook reaching into their UI state.
export default function useChores(token, hasHousehold) {
  const [chores, setChores] = useState([]);
  const [logs, setLogs] = useState([]);
  const [roommates, setRoommates] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [archivedChores, setArchivedChores] = useState([]);

  const fetchChores = async () => {
    setLoading(true);
    const minWait = new Promise(resolve => setTimeout(resolve, 500));
    try {
      const fetchPromise = api.get('/chores');
      const [res] = await Promise.all([fetchPromise, minWait]);
      setChores(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.get('/chores/logs');
      setLogs(res.data);
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

  const fetchRoommates = async () => {
    try {
      const res = await api.get('/household/roommates');
      setRoommates(res.data);
    } catch (err) {
      console.error("Error fetching roommates:", err);
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await api.get('/user/me');
      setUserData(res.data);
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  };

  // Split from the household-gated fetches below: onboarding needs userData
  // (the hasSeenWelcome/hasSeenTour flags) before a household exists.
  useEffect(() => {
    if (token) fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && hasHousehold) {
      fetchChores();
      fetchLogs();
      fetchRoommates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasHousehold]);

  const addChore = async (choreData) => {
    try {
      await api.post('/chores', choreData);
      await fetchChores();
      return true;
    } catch (err) {
      // This is where your "Chore already exists" message lives!
      alert(err.response?.data?.error || "Failed to add chore");
      return false;
    }
  };

  // Archives a chore (soft delete) — history/points are untouched, it just
  // drops off the active list and can be restored later.
  const archiveChore = async (id) => {
    try {
      await api.delete(`/chores/${id}`);
      await fetchChores();
      return true;
    } catch (err) {
      console.error("Error archiving chore", err);
      alert(err.response?.data?.error || "Failed to archive chore");
      return false;
    }
  };

  const fetchArchivedChores = async () => {
    try {
      const res = await api.get('/chores/archived');
      setArchivedChores(res.data);
    } catch (err) {
      console.error("Error fetching archived chores", err);
    }
  };

  const restoreChore = async (id) => {
    try {
      await api.patch(`/chores/${id}/restore`);
      await Promise.all([fetchChores(), fetchArchivedChores()]);
      return true;
    } catch (err) {
      console.error("Error restoring chore", err);
      alert(err.response?.data?.error || "Failed to restore chore");
      return false;
    }
  };

  // Permanently deletes a chore and its full log/grade history. Irreversible —
  // unlike archiveChore, this is the one path that actually moves point totals.
  const purgeChore = async (id) => {
    try {
      await api.delete(`/chores/${id}/purge`);
      await Promise.all([fetchChores(), fetchArchivedChores()]);
      return true;
    } catch (err) {
      console.error("Error permanently deleting chore", err);
      alert(err.response?.data?.error || "Failed to permanently delete chore");
      return false;
    }
  };

  const toggleFavorite = async (id, isFavorite) => {
    try {
      await api.patch(`/chores/${id}/favorite`, { isFavorite });
      fetchChores();
    } catch (err) {
      console.error("Error toggling favorite", err);
    }
  };

  const updateChore = async (id, data) => {
    try {
      await api.patch(`/chores/${id}`, data);
      await fetchChores();
      return true;
    } catch (err) {
      console.error("Error updating chore", err);
      alert(err.response?.data?.error || "Failed to update chore");
      return false;
    }
  };

  const completeChore = async (id, logData) => {
    try {
      await api.post(`/chores/${id}/complete`, logData);
      fetchChores();
      fetchLogs();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to complete chore");
    }
  };

  // Marks an onboarding carousel as seen server-side (not localStorage), so it
  // stays dismissed for this account across every device, not just this one.
  const markOnboardingSeen = async (field) => {
    try {
      await api.patch('/user/me', { [field]: true });
      await fetchUserData();
      return true;
    } catch (err) {
      console.error(`Error marking ${field} seen`, err);
      return false;
    }
  };

  const { gradeLog, editLog, deleteLog, editGrade } = useLogMutations(fetchLogs);

  return {
    chores, logs, roommates, userData, loading, archivedChores,
    fetchChores, fetchLogs, fetchRoommates, fetchUserData, fetchArchivedChores,
    addChore, archiveChore, restoreChore, purgeChore, toggleFavorite, updateChore,
    completeChore, gradeLog, editLog, deleteLog, editGrade, markOnboardingSeen,
  };
}
